import React, { memo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';
import { SkillIcon, TrackIcon } from 'atoms';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { IGrowthTrack } from 'core';
import { PATHS } from 'router/config';

interface IGrowthTracks {
  tracks: IGrowthTrack[];
  onChoose: (code: string) => Promise<boolean>;
  choosing: boolean;
  /**
   * Предпросмотр методиста (ТЗ 0039 доводка): треки видно, выбрать нельзя —
   * выбор хранится на студенте, которого у смотрящего нет.
   */
  readOnly?: boolean;
}

/**
 * Карьерные треки (ТЗ 0039 шаг 4).
 *
 * Продающая петля целиком: шаги показывают «2 из 3», а недостающие навыки —
 * чего не хватает и какой курс это даёт. Именно ради этого экрана раздел
 * открыт всей школе, а не только выпускникам карьерного центра.
 */
export const GrowthTracks = memo(
  ({ tracks, onChoose, choosing, readOnly = false }: IGrowthTracks) => {
    const { t } = useTranslation();

    if (!tracks.length) return null;

    return (
      <section>
        <SectionTitle>{t('growth.tracks')}</SectionTitle>
        <Grid>
          {tracks.map((track) => (
            <Card key={track.code} chosen={track.chosen}>
              <CardHead>
                {/* Знак профессии: официального логотипа у роли не бывает,
                    поэтому фигура выбрана по смыслу и приходит с сервера. В
                    сетке из шести карточек взгляду нужна опора помимо текста —
                    у медалей рядом она уже есть.

                    🔴 Тусклого варианта здесь НЕТ, хотя у медали он есть.
                    Медаль тускнеет, потому что не получена, — это реальное
                    состояние. Невыбранная профессия не «хуже» выбранной, и
                    серые пять карточек из шести убили бы ровно то различие,
                    ради которого знаки и заводились. Что трек выбран, говорит
                    оформление самой карточки. */}
                <TrackTitle>
                  <TrackIcon icon={track.icon} size={28} />
                  {track.title}
                </TrackTitle>
                <Progress>
                  {t('growth.stepsProgress', {
                    done: track.stepsDone,
                    total: track.stepsTotal,
                  })}
                </Progress>
              </CardHead>
              {track.description ? <Description>{track.description}</Description> : null}

              <Steps>
                {track.steps.map((step) => (
                  <Step key={step.title} satisfied={step.satisfied}>
                    <Mark satisfied={step.satisfied} />
                    <StepText>
                      {step.title}
                      {step.target > 1 && !step.satisfied ? (
                        <StepCount>
                          {' '}
                          {/* «Резюме заполнено 70 из 100» читалось как «70 из
                            100 полей», хотя это проценты. Единицу измерения
                            присылает сервер вместе с правилом. */}
                          {step.progressKind === 'percent'
                            ? t('growth.stepPercent', { n: step.current })
                            : t('growth.stepCount', {
                                current: step.current,
                                target: step.target,
                              })}
                        </StepCount>
                      ) : null}
                      {/* Пояснение шага приезжало с сервера и НЕ рисовалось
                          вовсе: «Профиль заполнен» без него не объясняет, что
                          именно считается заполненным. */}
                      {step.description ? <StepHint>{step.description}</StepHint> : null}
                    </StepText>
                  </Step>
                ))}
              </Steps>

              {track.missingSkills.length ? (
                <Missing>
                  <MissingLabel>{t('growth.missingSkills')}</MissingLabel>
                  <Chips>
                    {track.missingSkills.map((skill) => (
                      // Иконка и переход на страницу навыка: чип без того и
                      // другого — тупик, из которого не видно, что это за
                      // навык и где его взять.
                      <SkillChip key={skill.id} to={PATHS.growthSkill.replace(':slug', skill.slug)}>
                        <SkillIcon slug={skill.slug} name={skill.name} size={16} />
                        {skill.name}
                      </SkillChip>
                    ))}
                  </Chips>
                </Missing>
              ) : null}

              {track.recommendedCourses.length ? (
                <Missing>
                  <MissingLabel>{t('growth.whereToGet')}</MissingLabel>
                  <Chips>
                    {track.recommendedCourses.map((course) => (
                      // Курс уходит на сайт школы: витрины курсов в ЛМС нет,
                      // и рекомендация без перехода обрывается на названии.
                      <CourseChip
                        key={course.id}
                        href={course.url}
                        target="_blank"
                        rel="noreferrer">
                        {course.name}
                      </CourseChip>
                    ))}
                  </Chips>
                </Missing>
              ) : null}

              {readOnly ? null : (
                <ChooseButton
                  type="button"
                  chosen={track.chosen}
                  disabled={choosing || track.chosen}
                  onClick={() => onChoose(track.code)}>
                  {track.chosen ? t('growth.chosen') : t('growth.chooseTrack')}
                </ChooseButton>
              )}
            </Card>
          ))}
        </Grid>
      </section>
    );
  },
);

const SectionTitle = styled.h2`
  margin: 0 0 14px;
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div<{ chosen: boolean }>`
  padding: 18px 20px;
  border-radius: 12px;
  border: 1px solid ${({ chosen }) => (chosen ? Colors.main : Colors.gray)};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const TrackTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  ${fontService.text({ type: TextType.normal, weight: FontWeight.bold })}
`;

const Progress = styled.span`
  color: ${Colors.gray_dark};
  white-space: nowrap;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Description = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Steps = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Step = styled.li<{ satisfied: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  color: ${({ satisfied }) => (satisfied ? Colors.black : Colors.gray_dark)};
`;

/* Галочка рисуется бордерами, а не глифом: шрифт грузится с диска и «✓» в нём
   может не оказаться — в интерфейсе это даёт квадрат-тофу. */
const Mark = styled.span<{ satisfied: boolean }>`
  flex: none;
  width: 14px;
  height: 14px;
  margin-top: 3px;
  border-radius: 50%;
  border: 2px solid ${({ satisfied }) => (satisfied ? Colors.signal_green : Colors.gray)};
  position: relative;

  ${({ satisfied }) =>
    satisfied &&
    `
    &::after {
      content: '';
      position: absolute;
      left: 3px;
      top: 0;
      width: 4px;
      height: 8px;
      border-right: 2px solid ${Colors.signal_green};
      border-bottom: 2px solid ${Colors.signal_green};
      transform: rotate(45deg);
    }
  `}
`;

const StepText = styled.span`
  ${fontService.text({ type: TextType.small })}
`;

const StepHint = styled.span`
  display: block;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const StepCount = styled.span`
  color: ${Colors.gray_dark};
`;

const Missing = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MissingLabel = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.extraSmall, weight: FontWeight.medium })}
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  padding: 3px 10px;
  border-radius: 12px;
  background: ${Colors.bg_gray};
  ${fontService.text({ type: TextType.extraSmall })}
`;

const SkillChip = styled(Chip.withComponent(Link))`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${Colors.black};

  &:hover {
    color: ${Colors.black};
    background: ${Colors.gray};
  }
`;

/* Жёлтая рамка — ЗАЛИВКА не нужна, но и текст жёлтым не красим (правило
   палитры в context/constants/colors.ts): подпись остаётся чёрной. */
const CourseChip = styled(Chip.withComponent('a'))`
  border: 1px solid ${Colors.main};
  color: ${Colors.black};

  &:hover {
    color: ${Colors.black};
    background: ${Colors.gray};
  }
`;

const ChooseButton = styled.button<{ chosen: boolean }>`
  margin-top: auto;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: ${({ chosen }) => (chosen ? 'default' : 'pointer')};
  border: 1px solid ${Colors.main};
  background: ${({ chosen }) => (chosen ? Colors.main : 'transparent')};
  /* Невыбранная кнопка — жёлтая РАМКА и обычный текст. Жёлтым по белому
     контраст 1.33:1 при норме 4.5:1 — подпись «выбрать целью» на светлой теме
     не читалась (правило в context/constants/colors.ts). */
  color: ${({ chosen }) => (chosen ? Colors.btn_text : Colors.black)};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:disabled {
    opacity: ${({ chosen }) => (chosen ? 1 : 0.6)};
  }
`;
